/**
 * Trunnion AI - Classification Level Utilities
 * Colors, rank comparison, and helper functions for classification levels.
 */

import { CLASSIFICATION_CONFIG, CLASSIFICATION_LEVELS } from '@/config/constants';
import type { ClassificationLevel } from '@/types';

const FALLBACK_CONFIG = {
  label: 'Unclassified',
  shortLabel: 'U',
  color: '#4ADE80',
  bgColor: 'bg-classification-unclassified/10',
  borderColor: 'border-classification-unclassified',
  textColor: 'text-classification-unclassified',
  bannerText: 'UNCLASSIFIED',
  rank: 0,
};

function getConfig(level: ClassificationLevel) {
  return CLASSIFICATION_CONFIG[level] || FALLBACK_CONFIG;
}

/**
 * Get the numeric rank for a classification level (higher = more classified)
 */
export function getClassificationRank(level: ClassificationLevel): number {
  return getConfig(level).rank;
}

/**
 * Compare two classification levels.
 * Returns negative if a < b, 0 if equal, positive if a > b.
 */
export function compareClassification(
  a: ClassificationLevel,
  b: ClassificationLevel,
): number {
  return getClassificationRank(a) - getClassificationRank(b);
}

/**
 * Check if a classification level meets or exceeds the required level
 */
export function meetsClassification(
  operatorLevel: ClassificationLevel,
  requiredLevel: ClassificationLevel,
): boolean {
  return getClassificationRank(operatorLevel) >= getClassificationRank(requiredLevel);
}

/**
 * Get the highest classification level from a list
 */
export function getHighestClassification(
  levels: ClassificationLevel[],
): ClassificationLevel {
  if (levels.length === 0) return 'UNCLASSIFIED';
  return levels.reduce((highest, current) =>
    compareClassification(current, highest) > 0 ? current : highest,
  );
}

/**
 * Get color for a classification level
 */
export function getClassificationColor(level: ClassificationLevel): string {
  return getConfig(level).color;
}

/**
 * Get CSS class names for classification styling
 */
export function getClassificationClasses(level: ClassificationLevel): {
  bg: string;
  border: string;
  text: string;
} {
  const config = getConfig(level);
  return {
    bg: config.bgColor,
    border: config.borderColor,
    text: config.textColor,
  };
}

/**
 * Get banner text for a classification level
 */
export function getClassificationBannerText(level: ClassificationLevel): string {
  return getConfig(level).bannerText;
}

/**
 * Get the label for a classification level
 */
export function getClassificationLabel(level: ClassificationLevel): string {
  return getConfig(level).label;
}

/**
 * Get the short label for a classification level
 */
export function getClassificationShortLabel(level: ClassificationLevel): string {
  return getConfig(level).shortLabel;
}

/**
 * Get all allowed classification levels at or below a given level
 */
export function getAllowedClassifications(
  maxLevel: ClassificationLevel,
): ClassificationLevel[] {
  const maxRank = getClassificationRank(maxLevel);
  return CLASSIFICATION_LEVELS.filter(
    (level) => getClassificationRank(level) <= maxRank,
  );
}

/**
 * Get CSS class suffix for classification level
 */
export function getClassificationCssKey(level: ClassificationLevel): string {
  return level.toLowerCase().replace('_', '-');
}
